/**
 * The last workspace a person opened in each organization — a per-browser convenience for the
 * organization picker (plan-014 A4, FR12-AUTH-10: "last-used selection … always recheck access").
 *
 * It is a HINT and nothing more: `defaultScopeTargetOf` (`./target.ts`) uses it only when the fresh
 * access listing still offers that workspace as enterable, so a workspace the person was removed from,
 * or that was archived, is never opened from here. Kept in `localStorage` (ids only, never a name),
 * and every read and write is wrapped: a private window or blocked storage simply has no hint.
 */
const KEY_PREFIX = "wetalk.last-used-workspace.v1";

function keyOf(personId: string, accountId: string): string {
  return `${KEY_PREFIX}.${personId}.${accountId}`;
}

/** The slice of `Storage` the hint uses. */
export interface HintStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStorage(): HintStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function lastUsedWorkspaceOf(
  personId: string,
  accountId: string,
  storage: HintStorage | null = browserStorage(),
): string | null {
  try {
    return storage?.getItem(keyOf(personId, accountId)) ?? null;
  } catch {
    return null;
  }
}

export function rememberLastUsedWorkspace(
  personId: string,
  accountId: string,
  workspaceId: string,
  storage: HintStorage | null = browserStorage(),
): void {
  try {
    storage?.setItem(keyOf(personId, accountId), workspaceId);
  } catch {
    // Storage full or refused: the picker opens the first enterable workspace instead, as with no hint.
  }
}
